import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, doc, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase.ts";
import { useSession } from "../lib/session.tsx";
import { evidenceVisibilityConstraint } from "../lib/evidenceVisibility.ts";
import { ELECTION_DAY, RUNOFF_DAY } from "../features/briefing/targets.ts";

/**
 * The next-best-move assistant.
 *
 * A floating advisor that watches the campaign's live state and proposes the
 * highest-leverage next action. It is rules-based, not a language model: every
 * suggestion is a deterministic read of real data (an open conduct alert,
 * pending evidence, a narrative that broke into news, an approaching deadline),
 * so it can never invent a task that isn't grounded in what's actually there —
 * the same discipline the advisor runs on, applied to workflow.
 *
 * Priority 1 = act now, 2 = soon, 3 = housekeeping. Context nudges: a
 * suggestion whose page you're already on drops down the list.
 */

interface Suggestion {
  id: string;
  priority: 1 | 2 | 3;
  icon: string;
  title: string;
  href: string;
}

/**
 * The clock the assistant counts against. Filing close (17 August 2026) is
 * long past; what organises the day now is the general, and the runoff after
 * it, from the one place that owns those dates.
 */
const ELECTION = new Date(`${ELECTION_DAY}T00:00:00-06:00`).getTime();
const RUNOFF = new Date(`${RUNOFF_DAY}T00:00:00-06:00`).getTime();

function daysTo(ms: number): number {
  return Math.max(0, Math.ceil((ms - Date.now()) / 86400000));
}

export function Assistant() {
  const { t, profile, can } = useSession();
  const org = profile?.organizationId;
  const loc = useLocation();

  const [open, setOpen] = useState(false);
  const [conduct, setConduct] = useState(0);
  const [pendingEvidence, setPendingEvidence] = useState(0);
  const [citable, setCitable] = useState(0);
  const [breakouts, setBreakouts] = useState(0);
  const [flags, setFlags] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [hasObjectives, setHasObjectives] = useState(true);
  const [goals, setGoals] = useState(0);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // --- Live reads, each gated by permission ------------------------------
  useEffect(() => {
    if (!org) return;
    const subs: Array<() => void> = [];

    subs.push(
      onSnapshot(
        query(
          collection(db, "evidence"),
          where("organizationId", "==", org),
          ...evidenceVisibilityConstraint(can("evidence.restricted.view")),
          limit(500),
        ),
        (s) => {
          const docs = s.docs.map((d) => d.data());
          setPendingEvidence(docs.filter((e) => e.approvalStatus === "pending_review").length);
          setCitable(
            docs.filter((e) => e.approvalStatus === "approved" && e.reliability !== "pending")
              .length,
          );
        },
        () => {},
      ),
    );

    if (can("aura.query")) {
      subs.push(
        onSnapshot(
          query(
            collection(db, "mentions"),
            where("organizationId", "==", org),
            where("principalConduct", "==", true),
            limit(25),
          ),
          (s) => setConduct(s.docs.filter((d) => !d.get("conductAcknowledgedAt")).length),
          () => {},
        ),
      );
      subs.push(
        onSnapshot(
          query(collection(db, "narratives"), where("organizationId", "==", org), limit(25)),
          (s) =>
            setBreakouts(
              s.docs.filter((d) => d.get("breakout") === true && !d.get("breakoutAcknowledgedAt"))
                .length,
            ),
          () => {},
        ),
      );
      subs.push(
        onSnapshot(
          doc(db, "workingMemory", profile!.uid),
          (d) => {
            setHasObjectives(d.exists() && (d.get("objectives")?.length ?? 0) > 0);
          },
          () => {},
        ),
      );
    }
    if (can("compliance.review")) {
      subs.push(
        onSnapshot(
          query(
            collection(db, "complianceFlags"),
            where("organizationId", "==", org),
            where("status", "==", "open"),
            limit(50),
          ),
          (s) => setFlags(s.size),
          () => {},
        ),
      );
    }
    if (can("projects.view")) {
      subs.push(
        onSnapshot(
          query(collection(db, "tasks"), where("organizationId", "==", org), limit(200)),
          (s) => {
            const now = Date.now();
            setOverdue(
              s.docs.filter(
                (d) =>
                  d.get("status") !== "done" && (d.get("dueAt")?.toMillis?.() ?? Infinity) < now,
              ).length,
            );
          },
          () => {},
        ),
      );
    }
    if (can("projects.manage")) {
      subs.push(
        onSnapshot(
          query(collection(db, "goals"), where("organizationId", "==", org), limit(20)),
          (s) => setGoals(s.size),
          () => {},
        ),
      );
    }
    return () => subs.forEach((u) => u());
  }, [org, can, profile]);

  const suggestions = useMemo<Suggestion[]>(() => {
    const out: Suggestion[] = [];
    const electionDays = daysTo(ELECTION);
    const runoffDays = daysTo(RUNOFF);

    if (conduct > 0)
      out.push({
        id: "conduct",
        priority: 1,
        icon: "⚠",
        title: t("asst.s.conduct", { n: conduct }),
        href: "/watchdog",
      });

    if (breakouts > 0)
      out.push({
        id: "breakout",
        priority: 1,
        icon: "📈",
        title: t("asst.s.breakout"),
        href: "/watchdog",
      });

    if (electionDays > 0 && electionDays <= 14)
      out.push({
        id: "election",
        priority: 1,
        icon: "⏳",
        title: t("asst.s.election", { n: electionDays }),
        href: "/calendar",
      });
    else if (electionDays === 0 && runoffDays > 0 && runoffDays <= 21)
      out.push({
        id: "runoff",
        priority: 1,
        icon: "⏳",
        title: t("asst.s.runoff", { n: runoffDays }),
        href: "/calendar",
      });

    if (citable === 0)
      out.push({
        id: "noevidence",
        priority: 1,
        icon: "📄",
        title: t("asst.s.noEvidence"),
        href: "/evidence",
      });
    else if (pendingEvidence > 0)
      out.push({
        id: "pending",
        priority: 2,
        icon: "📄",
        title: t("asst.s.pending", { n: pendingEvidence }),
        href: "/evidence",
      });

    if (flags > 0)
      out.push({
        id: "flags",
        priority: 2,
        icon: "🚩",
        title: t("asst.s.flags", { n: flags }),
        href: "/finance",
      });

    if (can("aura.query") && !hasObjectives)
      out.push({
        id: "objectives",
        priority: 2,
        icon: "🎯",
        title: t("asst.s.objectives"),
        href: "/memory",
      });

    if (can("projects.manage") && goals === 0)
      out.push({
        id: "goals",
        priority: 2,
        icon: "🏁",
        title: t("asst.s.goals"),
        href: "/projects",
      });

    if (overdue > 0)
      out.push({
        id: "overdue",
        priority: 3,
        icon: "🕐",
        title: t("asst.s.overdue", { n: overdue }),
        href: "/work",
      });

    return (
      out
        .filter((s) => !dismissed.has(s.id))
        // De-prioritize a suggestion whose page you're already viewing.
        .sort((a, b) => {
          const ap = a.priority + (loc.pathname === a.href ? 5 : 0);
          const bp = b.priority + (loc.pathname === b.href ? 5 : 0);
          return ap - bp;
        })
    );
  }, [
    conduct,
    breakouts,
    citable,
    pendingEvidence,
    flags,
    hasObjectives,
    goals,
    overdue,
    dismissed,
    loc.pathname,
    can,
    t,
  ]);

  const urgent = suggestions.filter((s) => s.priority === 1).length;

  return (
    <>
      <button
        type="button"
        className="asst-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          urgent > 0 ? `${t("asst.title")} — ${t("asst.urgent", { n: urgent })}` : t("asst.title")
        }
        aria-expanded={open}
      >
        ✦
        {urgent > 0 && (
          <span className="asst-badge" aria-hidden="true">
            {urgent}
          </span>
        )}
      </button>

      {open && (
        <div className="asst-panel" role="dialog" aria-label={t("asst.title")}>
          <div className="card-head">
            <div>
              <h3>{t("asst.title")}</h3>
              <span className="faint small">{t("asst.subtitle")}</span>
            </div>
            <button type="button" className="subtle" onClick={() => setOpen(false)}>
              ×
            </button>
          </div>

          {suggestions.length === 0 ? (
            <div className="empty small">{t("asst.none")}</div>
          ) : (
            suggestions.slice(0, 6).map((s) => (
              <div
                key={s.id}
                className="asst-item"
                style={{
                  borderInlineStartColor:
                    s.priority === 1
                      ? "var(--danger)"
                      : s.priority === 2
                        ? "var(--warn)"
                        : "var(--text-faint)",
                }}
              >
                <div className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: "1rem" }}>{s.icon}</span>
                  <span className="small" style={{ flex: 1 }}>
                    {s.title}
                  </span>
                </div>
                <div className="row row-end asst-actions">
                  <button
                    type="button"
                    className="subtle"
                    onClick={() => setDismissed(new Set([...dismissed, s.id]))}
                  >
                    {t("asst.dismiss")}
                  </button>
                  {/* A link, not a <button> inside a <Link>: one control, one role. */}
                  <Link to={s.href} className="asst-go" onClick={() => setOpen(false)}>
                    {t("asst.go")}
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}
